import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useUser } from '@descope/react-sdk';
import {
  muteAccountAlert,
  muteOrganizationAlert,
  unmuteAccountAlert,
  unmuteOrganizationAlert,
} from '@kirin/api-client/api/alerts';
import type { MutedAlertState } from '@kirin/api-client/contracts/alerts';
import { apiClient } from '@/lib/api-client';
import { normalizeDescopeUser } from '@/lib/descopeUser';
import {
  applyOptimisticPatch,
  cancelOverlappingQueries,
  invalidateAffectedQueries,
  restoreSnapshot,
  type AlertSuppressionScope,
  type CacheSnapshot,
} from './alertSuppressionCache';

/**
 * Mute scope discriminator. Re-exported as a type alias of
 * {@link AlertSuppressionScope} so existing call sites importing
 * `AlertMuteScope` keep compiling without churn.
 *
 * - `organization`: scopes the mutation to an org-shared alert. Caller must
 *   already be authorized (owner/manager) by the time the hook is called.
 * - `account`: scopes the mutation to the authenticated user's own alerts.
 */
export type AlertMuteScope = AlertSuppressionScope;

/** Variables for {@link useMuteAlert}. */
export interface MuteAlertVariables {
  alertKey: string;
  /** ISO datetime in the future, or `null` to mute indefinitely. */
  mutedUntil: string | null;
}

/** Variables for {@link useUnmuteAlert}. */
export interface UnmuteAlertVariables {
  alertKey: string;
}

/**
 * Canonical "not muted" projection. Mirrors the backend `UNMUTED_DEFAULT`
 * exported from `apps/backend/src/domain/handlers/alerts/alerts-helpers.ts`.
 *
 * Used to optimistically restore the unmuted shape during `useUnmuteAlert`
 * and as the rollback target. The shape MUST stay aligned with
 * `mutedAlertStateSchema` from `@kirin/api-client/contracts/alerts`.
 */
export const UNMUTED_DEFAULT: MutedAlertState = Object.freeze({
  muted: false,
  mutedAt: null,
  mutedUntil: null,
  mutedByUserId: null,
  mutedByEmail: null,
  mutedByName: null,
});

/**
 * Build the optimistic `MutedAlertState` shown to the user between
 * "click mute" and the server response.
 *
 * The optimistic shape projects the muting actor's identity from the
 * currently-authenticated Descope user so the Unmute tooltip reads
 * `"Muted by <real name>"` immediately — closing the brief
 * `"Muted by Unknown user"` flash that surfaced on main (KIR-2747).
 *
 * `mutedByUserId` stays `null` because the frontend only knows the Descope
 * external identifier, not the Kirin user UUID; the contract permits
 * `null` here (`mutedByUserId: z.uuid().nullable()`). The server reconciles
 * the real UUID — plus authoritative `mutedByEmail` / `mutedByName` — via
 * the standard invalidation pass on `onSettled`.
 *
 * When `currentUser` is `null` (no live session — never reachable through
 * the UI but defensible), the optimistic patch falls back to the prior
 * shape: every identity field `null`, so `formatMutedTooltipLines` renders
 * `"Muted by Unknown user"`. Graceful degradation, not a regression.
 *
 * Empty strings are normalised to `null` on the way out so the optimistic
 * shape matches `mutedAlertStateSchema` (`mutedByEmail: z.email().nullable()`,
 * `mutedByName: z.string().nullable()`) — important for any future refactor
 * that routes optimistic writes through `parseSchema`.
 */
function buildOptimisticMute(
  mutedUntil: string | null,
  currentUser: { name: string; email: string } | null
): MutedAlertState {
  return {
    muted: true,
    mutedAt: new Date().toISOString(),
    mutedUntil,
    mutedByUserId: null,
    mutedByEmail: currentUser?.email ? currentUser.email : null,
    mutedByName: currentUser?.name ? currentUser.name : null,
  };
}

/**
 * React Query mutation hook to mute a grouped alert.
 *
 * Optimistically patches the alert's `mute` fragment in every grouped-alerts
 * list and alert-occurrences cache entry in scope, captures the affected
 * snapshots, and rolls them back on error. Always invalidates lists,
 * occurrences, and (org-scope) USP on settle so the server's authoritative
 * state — including `mutedByEmail`/`mutedByName` — replaces the placeholder
 * in-memory shape.
 *
 * **Optimistic identity (KIR-2747).** The hook reads the current Descope
 * user via `useUser()` at hook scope, normalises it through
 * {@link normalizeDescopeUser}, and threads it into the optimistic patch so
 * the Unmute tooltip immediately reads `"Muted by <real name>"`. The
 * `mutedByUserId` field stays `null` on the optimistic patch because the
 * frontend only has the Descope external ID, not the Kirin user UUID — the
 * server fills that in on the post-settle refetch. Reading `useUser()`
 * inside `onMutate` would violate the Rules of Hooks; the resolved value
 * is captured in the closure so the callback can use it safely.
 *
 * **Not flag-gated.** The hook is a pure data-layer helper; gating belongs at
 * the call site (UI button) so unrelated callers cannot accidentally bypass
 * the rule. The corresponding API routes 404 when the `alert_muting` feature
 * flag is disabled, so misuse fails closed even without UI gating.
 */
export function useMuteAlert(
  scope: AlertMuteScope
): UseMutationResult<
  MutedAlertState,
  Error,
  MuteAlertVariables,
  CacheSnapshot
> {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const currentUser = normalizeDescopeUser(user);

  return useMutation<MutedAlertState, Error, MuteAlertVariables, CacheSnapshot>(
    {
      mutationFn: ({ alertKey, mutedUntil }) => {
        if (scope.kind === 'organization') {
          return muteOrganizationAlert(
            apiClient,
            scope.organizationId,
            alertKey,
            {
              mutedUntil,
            }
          );
        }
        return muteAccountAlert(apiClient, alertKey, { mutedUntil });
      },
      onMutate: async ({ alertKey, mutedUntil }) => {
        await cancelOverlappingQueries(queryClient, scope, alertKey);
        return applyOptimisticPatch(queryClient, scope, alertKey, {
          fragment: 'mute',
          value: buildOptimisticMute(mutedUntil, currentUser),
        });
      },
      onError: (_error, _variables, context) => {
        if (context) {
          restoreSnapshot(queryClient, context);
        }
      },
      onSettled: (_data, _error, variables) => {
        invalidateAffectedQueries(
          queryClient,
          scope,
          variables.alertKey,
          'mute'
        );
      },
    }
  );
}

/**
 * React Query mutation hook to unmute a grouped alert.
 *
 * Optimistically replaces the alert's `mute` fragment with
 * {@link UNMUTED_DEFAULT} in every grouped-alerts list and alert-occurrences
 * cache entry in scope, captures snapshots, and rolls them back on error.
 * Always invalidates lists, occurrences, and (org-scope) USP on settle.
 *
 * **Not flag-gated** — see {@link useMuteAlert}.
 */
export function useUnmuteAlert(
  scope: AlertMuteScope
): UseMutationResult<
  MutedAlertState,
  Error,
  UnmuteAlertVariables,
  CacheSnapshot
> {
  const queryClient = useQueryClient();

  return useMutation<
    MutedAlertState,
    Error,
    UnmuteAlertVariables,
    CacheSnapshot
  >({
    mutationFn: ({ alertKey }) => {
      if (scope.kind === 'organization') {
        return unmuteOrganizationAlert(
          apiClient,
          scope.organizationId,
          alertKey
        );
      }
      return unmuteAccountAlert(apiClient, alertKey);
    },
    onMutate: async ({ alertKey }) => {
      await cancelOverlappingQueries(queryClient, scope, alertKey);
      return applyOptimisticPatch(queryClient, scope, alertKey, {
        fragment: 'mute',
        value: UNMUTED_DEFAULT,
      });
    },
    onError: (_error, _variables, context) => {
      if (context) {
        restoreSnapshot(queryClient, context);
      }
    },
    onSettled: (_data, _error, variables) => {
      invalidateAffectedQueries(queryClient, scope, variables.alertKey, 'mute');
    },
  });
}
